import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@staff-app/ui";
import type {
  DeviceOrderCoupon,
  DeviceOrderTask,
  OrderDeviceTab,
} from "@staff-app/shared-types";
import { DATA_TABLE_CELLS, DATA_TABLE_HEADER } from "@/lib/data-table";
import { ExternalLink } from "lucide-react";
import { MintCouponButton } from "./MintCouponButton";
import { DeactivateAccountButton } from "./DeactivateAccountButton";
import { DismissOrderButton, DismissedOrderState } from "./DismissOrderButton";
import { ReplaceCouponButton } from "./ReplaceCouponButton";
import { canonStatus } from "../lib/status";
import { OrderNotes } from "./OrderNotes";

function fmtDate(d?: string | null): string {
  if (!d) return "—";
  const parsed = new Date(d);
  return isNaN(+parsed) ? "—" : parsed.toLocaleDateString();
}

interface DeviceRow {
  name: string;
  qty: string;
  index: number; // products[] line index
  coupon?: DeviceOrderCoupon; // the coupon for THIS device line, if any
}

/**
 * Split an order into ONE row per device line — each device gets its own coupon,
 * so each is its own row (the invoice-level columns repeat across an order's rows).
 * products[] entries look like "5x Optisigns Android Device Pre-Configured". Coupons
 * are matched to a line by `productIndex` (falling back to array position for legacy
 * coupons minted before that field existed). A legacy order with no products[] yields
 * a single row from deviceType/qty.
 */
function deviceRows(task: DeviceOrderTask): DeviceRow[] {
  const products = task.products ?? [];
  const coupons = task.coupons ?? [];
  // Match strictly by productIndex. The positional fallback exists only for LEGACY coupons
  // minted before that field existed, so it applies only when NO coupon carries one —
  // otherwise a duplicate/missing index would display one device's code against another.
  const anyIndexed = coupons.some((c) => c.productIndex != null);
  const couponFor = (i: number, n: number): DeviceOrderCoupon | undefined =>
    coupons.find((c) => c.productIndex === i) ??
    (!anyIndexed && coupons.length === n ? coupons[i] : undefined);
  if (products.length === 0) {
    return [
      {
        name: task.deviceType || "—",
        qty: task.qty != null ? String(task.qty) : "—",
        index: 0,
        coupon: couponFor(0, 1),
      },
    ];
  }
  return products.map((p, i) => {
    const m = String(p).match(/^\s*(\d+|\?)x\s+(.+?)\s*$/);
    return {
      name: m ? m[2] : String(p),
      qty: m ? (m[1] === "?" ? "—" : m[1]) : "—",
      index: i,
      coupon: couponFor(i, products.length),
    };
  });
}

/**
 * Status pill — uniform size + font, centered text, so paid/open read as equals.
 *
 * The LABEL is verbatim: an ingested row shows exactly the word Stripe reports, so this page
 * and the Stripe dashboard never disagree, and a backfilled row still shows the word it was
 * recorded with. Only the colour is canonicalised, so both vocabularies look alike.
 */
function StatusBadge({ status }: { status?: string | null }) {
  const s = canonStatus(status);
  const variant =
    s === "paid"
      ? "default"
      : s === "uncollectible"
        ? "destructive"
        : s === "void"
          ? "outline"
          : "secondary";
  return (
    <Badge
      variant={variant as never}
      className="w-24 justify-center text-xs font-medium"
    >
      {status || "—"}
    </Badge>
  );
}

/**
 * Payment-gate state, stacked under the status pill rather than given its own column — the
 * table is already 13 columns wide, and this is a qualifier ON the status, not a peer of it.
 *
 * Only ever shown for an order whose coupon was issued before payment (the management
 * override). Everything else renders nothing.
 */
function EnforcementBadge({ task }: { task: DeviceOrderTask }) {
  if (!task.couponIssuedBeforePayment) return null;

  // Codes pulled instead of an account switched off — the deadline passed but the coupon had
  // never been redeemed, so there was nothing to recover. Checked BEFORE the deactivated state
  // because it is the terminal one: a cancelled order is finished, not still being chased.
  if (task.couponsCancelledAt) {
    return (
      <Badge
        variant={"outline" as never}
        className="w-24 justify-center text-[10px]"
        title={`Unpaid at the deadline and the code was never used, so it was cancelled in Shopify on ${new Date(
          task.couponsCancelledAt,
        ).toLocaleDateString()}. No account was switched off.`}
      >
        {"Code pulled"}
      </Badge>
    );
  }

  // Already switched off — the loudest state, and the one support will be asked about.
  if (task.deactivatedAt) {
    return (
      <Badge
        variant={"destructive" as never}
        className="w-24 justify-center text-[10px]"
        title={`Screens switched off ${new Date(task.deactivatedAt).toLocaleDateString()}${
          task.reenableNotifiedAt
            ? " — paid since; billing asked to re-enable"
            : ""
        }`}
      >
        {task.reenableNotifiedAt ? "Paid — re-enable" : "Screens off"}
      </Badge>
    );
  }

  // Enforcement wanted to act but could not identify the account. Needs a human.
  if (task.enforceError) {
    return (
      <Badge
        variant={"outline" as never}
        className="w-24 justify-center text-[10px]"
        title={task.enforceError}
      >
        {"Unresolved"}
      </Badge>
    );
  }

  // Net 60+ is deliberately never chased — the invoice is not even due by the deadline.
  const excluded = task.netTermsDays != null && task.netTermsDays >= 60;
  if (excluded) {
    return (
      <Badge
        variant={"secondary" as never}
        className="w-24 justify-center text-[10px]"
        title={`Net ${task.netTermsDays} — tracked but never auto-chased or switched off`}
      >
        {`Net ${task.netTermsDays}`}
      </Badge>
    );
  }

  if (!task.chaseDueDate) return null;
  const daysLeft = Math.ceil(
    (new Date(task.chaseDueDate).getTime() - Date.now()) / 86400_000,
  );
  return (
    <Badge
      variant={(daysLeft <= 10 ? "destructive" : "secondary") as never}
      className="w-24 justify-center text-[10px]"
      title={`Unpaid coupon approved by ${task.gateOverrideBy || "unknown"}${
        task.gateOverrideReason ? ` — "${task.gateOverrideReason}"` : ""
      }. Screens switch off ${new Date(task.chaseDueDate).toLocaleDateString()}.${
        task.warnedAt ? " Support has been asked to chase." : ""
      }`}
    >
      {daysLeft <= 0 ? "Overdue" : `${daysLeft}d to off`}
    </Badge>
  );
}

/** Coupon Code for ONE device line — its code, or the hold/gate state when none yet. */
function couponCodeFor(
  task: DeviceOrderTask,
  coupon?: DeviceOrderCoupon,
): string {
  if (coupon?.code) return coupon.code;
  if (task.provideGate === "HOLD") return "HOLD — do not give";
  if (task.provideGate === "GIVE_AFTER_PAYMENT") return "Give after payment";
  return "—";
}

export function OrderTaskTable({
  tasks,
  tab,
  onMinted,
}: {
  tasks: DeviceOrderTask[];
  tab: OrderDeviceTab;
  /** Forwarded to each row's mint button; the PAGE shows the confirmation, because a
   *  successful mint unmounts the row's button (it becomes "Issued"). */
  onMinted?: (c: {
    code: string;
    amount: number | null;
    deviceName?: string | null;
    customer: string;
  }) => void;
}) {
  if (tasks.length === 0) {
    return (
      <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
        {"No orders."}
      </div>
    );
  }
  return (
    <div className="min-h-0 flex-1 rounded-md border [&>div]:h-full">
      {/* min-w is kept: 13 columns genuinely need the width, so this table scrolls sideways where
          the orders table fits. Everything else — header tone, density, hover — is shared. */}
      <Table className={`min-w-[1400px] ${DATA_TABLE_CELLS}`}>
        <TableHeader className={DATA_TABLE_HEADER}>
          <TableRow>
            <TableHead>{"Date"}</TableHead>
            <TableHead>{"Customer Email"}</TableHead>
            <TableHead>{"Item Name"}</TableHead>
            <TableHead className="text-center">{"Quantity"}</TableHead>
            <TableHead>{"Invoice ID"}</TableHead>
            <TableHead>{"Status"}</TableHead>
            <TableHead>{"Paid Date"}</TableHead>
            <TableHead>{"Coupon Code"}</TableHead>
            <TableHead>{"Generated By"}</TableHead>
            <TableHead>{"Code Create Date"}</TableHead>
            <TableHead>{"Code Used"}</TableHead>
            <TableHead>{"Minted"}</TableHead>
            <TableHead>{"Actions"}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tasks.flatMap((t) =>
            deviceRows(t).map((d) => (
              <TableRow key={`${t._id}#${d.index}`} data-testid="order-row">
                <TableCell className="whitespace-nowrap">
                  {fmtDate(t.createdAt)}
                </TableCell>
                <TableCell className="font-medium">
                  {/* Owner first: `customerEmail` is Stripe's invoice email, which is null on
                      every backfilled row — those used to render as a bare cus_ id. */}
                  {t.ownerEmail || t.customerEmail || (
                    <span className="font-mono text-xs text-muted-foreground">
                      {t.stripeCustomerId || "—"}
                    </span>
                  )}
                </TableCell>
                <TableCell
                  className="max-w-[260px] truncate"
                  data-testid="order-item"
                  title={d.name}
                >
                  {d.name}
                </TableCell>
                <TableCell className="text-center" data-testid="order-qty">
                  {d.qty}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {(t.invoiceId || "").startsWith("in_") ? (
                    <a
                      href={`https://dashboard.stripe.com/invoices/${t.invoiceId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                      title="Open invoice in Stripe"
                    >
                      {t.invoiceId}
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                  ) : (
                    t.invoiceId || t._id
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-1">
                    <StatusBadge status={t.status} />
                    <EnforcementBadge task={t} />
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {fmtDate(t.paidAt)}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {couponCodeFor(t, d.coupon)}
                </TableCell>
                <TableCell className="text-xs">
                  {d.coupon?.mintedByStaffEmail || "—"}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {fmtDate(d.coupon?.mintedAt)}
                </TableCell>
                <TableCell>
                  {d.coupon ? (d.coupon.used ? "Yes" : "No") : "—"}
                </TableCell>
                <TableCell>
                  {d.coupon?.mintedByStaffEmail ? "Yes" : "—"}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {/* Notes are per-order, but shown on every device row so a
                        staffer looking at any one device sees the order's notes. */}
                    <OrderNotes invoiceId={t._id} />
                    {d.coupon?.code ? (
                      <>
                        <span className="text-xs text-muted-foreground">
                          {"Issued"}
                        </span>
                        {/* Root-only; hidden for everyone else and for a redeemed code. */}
                        <ReplaceCouponButton task={t} productIndex={d.index} coupon={d.coupon} />
                      </>
                    ) : t.dismissedAt ? (
                      // Coupon given by hand before the app — minting now would issue a second one.
                      <DismissedOrderState task={t} />
                    ) : (
                      <MintCouponButton
                        task={t}
                        productIndex={d.index}
                        deviceName={d.name}
                        onMinted={onMinted}
                      />
                    )}
                    {/* Root-only; paid orders in the Active queue whose coupon was given by hand.
                        Once per ORDER (its first uncouponed row), since dismissal clears the
                        whole order. */}
                    {tab === "ACTIVE" &&
                    !d.coupon?.code &&
                    d.index === deviceRows(t).find((x) => !x.coupon?.code)?.index ? (
                      <DismissOrderButton task={t} />
                    ) : null}
                    {/* Switching an account off is only ever justified by a REDEEMED coupon: the
                        customer has the hardware and has not paid. An unclaimed code costs us
                        nothing and the sweep cancels it instead, so the button is not offered
                        there. Unpaid-coupons tab only — on the other tabs there is nothing to
                        enforce. */}
                    {tab === "UNPAID_COUPONS" && d.coupon?.used ? (
                      <DeactivateAccountButton task={t} />
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            )),
          )}
        </TableBody>
      </Table>
    </div>
  );
}
